import { useCallback, useEffect, useRef, useState, type FC } from "react";

import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { FETCH_SETTINGS_DETAILS } from "@/queries";
import shopifyLogo from "@assets/shopify-mini-logo.png";
import { GenericTooltip } from "@components/tooltip-container";
import { useNorthbeamQuery } from "@hooks/http";
import {
  ConnectionType,
  type FetchSanitizedConnectionsByType,
  type FetchSanitizedConnectionsByTypeVariables,
  type FetchSettingsDetails,
} from "@north-beam/nb-client-side";
import { ShopifyConnectionCard } from "@pages/settings/connections/shopify";
import { FETCH_SANITIZED_CONNECTIONS_BY_TYPE } from "@pages/settings/queries";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import RadioGroup from "@shared/radio-group";

import { Error } from "../error";
import { Loading } from "../loading";
import {
  isVisible,
  scrollToSubStepElement,
} from "../onboarding-nav/scroll-utils";
import { useOnboardingStep } from "../onboarding-nav/use-onboarding-number";
import { Steps } from "../steps";

import { OrderApiTags } from "./orders-api/order-api-tags";
import { ShopifyOrderTags } from "./orders-api/shopify-order-tags";
import {
  getMissingSteps,
  MissingStepsModal,
  StepCompletionConfirmation,
} from "./orders-api/step-completion-confirmation";
import { type OrdersApiStepMetadata } from "./orders-api/step-metadata";
import { Step, type StepChildProps } from "./step";
import { StepContainer } from "./step-container";

export const Shopify = () => {
  const { data, loading, error } = useNorthbeamQuery<FetchSettingsDetails>(
    FETCH_SETTINGS_DETAILS,
    {},
  );

  return (
    <>
      {data ? (
        <ShopifyConnectionCard integrationDetails={data.integrationDetails} />
      ) : error ? (
        <Error content="There was an error loading your settings." />
      ) : loading ? (
        <Loading />
      ) : (
        <></>
      )}
    </>
  );
};

export const OrdersApi: FC<StepChildProps> = ({
  previousStep,
  nextStep,
  isMarkedComplete,
  ...props
}) => {
  const aScrollRef = useRef<(() => void) | null>(null);
  const bScrollRef = useRef<(() => void) | null>(null);
  const cScrollRef = useRef<(() => void) | null>(null);
  const dScrollRef = useRef<(() => void) | null>(null);
  const eScrollRef = useRef<(() => void) | null>(null);
  const { stepDictionary } = useOnboardingStep();
  const stepIndex = stepDictionary["orders-api"].index;
  const [searchParams, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    step: withDefault(StringParam, ""),
    provider: withDefault(StringParam, ""),
    noScroll: withDefault(BooleanParam, false),
  });

  const aTitleRef = useCallback(
    (node: any) => {
      if (node !== null) {
        aScrollRef.current = node;
        scrollToSubStepElement(node, searchParams, "a");
      }
    },
    [searchParams],
  );
  const bTitleRef = useCallback(
    (node: any) => {
      if (node !== null) {
        bScrollRef.current = node;
        scrollToSubStepElement(node, searchParams, "b");
      }
    },
    [searchParams],
  );
  const cTitleRef = useCallback(
    (node: any) => {
      if (node !== null) {
        cScrollRef.current = node;
        scrollToSubStepElement(node, searchParams, "c");
      }
    },
    [searchParams],
  );
  const dTitleRef = useCallback(
    (node: any) => {
      if (node !== null) {
        scrollToSubStepElement(node, searchParams, "d");
      }
    },
    [searchParams],
  );

  const eTitleRef = useCallback(
    (node: any) => {
      if (node !== null) {
        scrollToSubStepElement(node, searchParams, "e");
      }
    },
    [searchParams],
  );

  useEffect(() => {
    if (searchParams.noScroll) {
      setSearchParams({ noScroll: false });
    }
  }, []);

  const [stepMetadata, setStepMetadata] = useState<OrdersApiStepMetadata>(
    props.stepMetadata,
  );

  const updateStepMetadata = (stepMetadata: OrdersApiStepMetadata) => {
    props.updateStepMetadata(stepMetadata);
    setStepMetadata(stepMetadata);
  };

  const useShopify = stepMetadata?.shopifyOrCustom === "shopify";

  const type = ConnectionType.integration_shopify;
  const { data, loading: fetchLoading } = useNorthbeamQuery<
    FetchSanitizedConnectionsByType,
    FetchSanitizedConnectionsByTypeVariables
  >(FETCH_SANITIZED_CONNECTIONS_BY_TYPE, {
    variables: { type },
    skip: !useShopify,
  });
  const [isShopifyConnected, setIsShopifyConnected] = useState(false);
  useEffect(() => {
    if (fetchLoading) {
      return;
    }
    setIsShopifyConnected((data?.me.sanitizedConnections || []).length > 0);
  }, [data, fetchLoading]);

  const missingSteps = getMissingSteps(stepMetadata, isShopifyConnected);
  const [isMissingStepsModalOpen, setIsMissingStepsModalOpen] = useState(false);
  const goToNextStep = () => {
    if (!isMarkedComplete && missingSteps.length === 0) {
      props.toggleStepComplete();
    }
    setSearchParams({
      step: Steps.CONFIGURE_PLATFORMS,
    });
    nextStep && nextStep();
  };
  const onClickNextStep = () => {
    if (missingSteps.length > 0) {
      setIsMissingStepsModalOpen(true);
      return;
    }
    goToNextStep();
  };

  return (
    <StepContainer
      onScrollStopped={() => {
        if (isVisible(aScrollRef)) {
          setSearchParams({
            subStep: "a",
            noScroll: true,
          });
        } else if (isVisible(bScrollRef)) {
          setSearchParams({
            subStep: "b",
            noScroll: true,
          });
        } else if (isVisible(cScrollRef)) {
          setSearchParams({
            subStep: "c",
            noScroll: true,
          });
        } else if (isVisible(dScrollRef)) {
          setSearchParams({
            subStep: "d",
            noScroll: true,
          });
        } else if (isVisible(eScrollRef)) {
          setSearchParams({
            subStep: "e",
            noScroll: true,
          });
        }
      }}
      isLast={false}
      title={`${stepIndex}. Add Orders`}
      onMoveBackwardButtonClicked={() => {
        setSearchParams({
          step: Steps.PIXEL_PLACEMENT,
        });
        previousStep && previousStep();
      }}
      onMoveForwardButtonClicked={onClickNextStep}
      hideFooter={fetchLoading}
      knowledgeBaseLink="https://docs.northbeam.io/docs/add-orders"
      knowledgeBaseText="Add Orders"
      stepCompletionConfirmation={
        <StepCompletionConfirmation
          nextStep={goToNextStep}
          stepMetadata={stepMetadata}
          usesShopify={useShopify}
          toggleStepComplete={props.toggleStepComplete}
        />
      }
    >
      <Step
        loading={fetchLoading}
        nextStepButtonText={`${stepDictionary["configure-platforms"].index}. Configure Integration`}
        nextStep={onClickNextStep}
      >
        <MissingStepsModal
          isOpen={isMissingStepsModalOpen}
          onRequestClose={() => setIsMissingStepsModalOpen(false)}
          missingSteps={missingSteps}
          footer={
            <div className="flex items-center justify-end gap-2">
              <TertiaryButton
                onClick={() => {
                  setIsMissingStepsModalOpen(false);
                  goToNextStep();
                }}
                className="uppercase"
              >
                Skip for now
              </TertiaryButton>
              <PrimaryButton
                onClick={() => {
                  setIsMissingStepsModalOpen(false);
                }}
                className="uppercase"
              >
                Go back
              </PrimaryButton>
            </div>
          }
        />
        <div
          ref={aTitleRef}
          className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-10 inline-flex border-b-gray-1 border-b scroll-m-10"
        >
          <h1>What is your source of truth for Orders?</h1>
          <RadioGroup
            className="text-lg space-y-6"
            options={[
              {
                label: (
                  <GenericTooltip
                    content={
                      "Uncheck the 'Complete' checkbox at the bottom of the screen to update"
                    }
                    showTooltip={isMarkedComplete}
                    noInfoCircle
                    placement="top-start"
                  >
                    <div className="flex gap-2 items-center">
                      <img
                        src={shopifyLogo}
                        alt="shopify Logo"
                        className="w-5 h-5"
                      />
                      Shopify Storefront
                    </div>
                  </GenericTooltip>
                ),
                value: "shopify",
                disabled: isMarkedComplete,
              },
              {
                label: (
                  <GenericTooltip
                    content={
                      "Uncheck the 'Complete' checkbox at the bottom of the screen to update"
                    }
                    showTooltip={isMarkedComplete}
                    noInfoCircle
                    placement="top-start"
                  >
                    <div className="mt-4">
                      <div>
                        Other e-commerce platform (WooCommerce, Magneto) or
                        Custom solution (e.g Salesforce, BigQuery, or other Data
                        Lake Solutions)
                      </div>
                      <div className="text-sm font-semibold">
                        Only available on Pro and Enterprise Plans
                      </div>
                    </div>
                  </GenericTooltip>
                ),
                value: "custom",
                disabled: isMarkedComplete,
              },
            ]}
            value={stepMetadata?.shopifyOrCustom || ""}
            onChange={(value) => {
              if (value === "") {
                return;
              }
              updateStepMetadata({
                ...stepMetadata,
                shopifyOrCustom: value,
              });
            }}
          />
        </div>
        {useShopify && (
          <ShopifyOrderTags
            isMarkedComplete={isMarkedComplete}
            aTitleRef={aTitleRef}
            bTitleRef={bTitleRef}
            cTitleRef={cTitleRef}
            dTitleRef={dTitleRef}
            stepMetadata={stepMetadata}
            updateStepMetadata={updateStepMetadata}
            goToNamedStep={props.goToNamedStep}
          />
        )}
        {stepMetadata?.shopifyOrCustom === "custom" && (
          <OrderApiTags
            isMarkedComplete={isMarkedComplete}
            stepMetadata={stepMetadata}
            updateStepMetadata={updateStepMetadata}
            aTitleRef={aTitleRef}
            bTitleRef={bTitleRef}
            cTitleRef={cTitleRef}
            dTitleRef={dTitleRef}
            eTitleRef={eTitleRef}
          />
        )}
      </Step>
    </StepContainer>
  );
};
